import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useFormularios } from "@/hooks/useFormularios";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useUserClients } from "@/hooks/useUserClients";
import { supabase } from "@/integrations/supabase/client";
import { FormularioBuilder } from "./FormularioBuilder";
import { NewFormularioDialog } from "./NewFormularioDialog";
import { EditFormularioDialog } from "./EditFormularioDialog";
import { ClipboardList, Plus, MoreVertical, Edit, Copy, Archive, Trash, FileText, Users, Search, Settings, Eye } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Formulario } from "@/types/formulario";
import { FormularioRenderer } from './FormularioRenderer';
import { FormularioRespostasList } from './FormularioRespostasList';
import { useFormularioCampos } from '@/hooks/useFormularioCampos';
import { useFormularioRespostas } from '@/hooks/useFormularioRespostas';
import { FormularioDistribuicaoDialog } from './FormularioDistribuicaoDialog';
import { FormularioDistribuicoesList } from './FormularioDistribuicoesList';
import { Send } from 'lucide-react';

const FormulariosManagement = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const { clients } = useUserClients();
  const { formularios, loading, addFormulario, addFormularioAsync, updateFormulario, deleteFormulario, arquivarFormulario, duplicarFormulario } = useFormularios();
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFormulario, setSelectedFormulario] = useState<Formulario | null>(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderFormulario, setBuilderFormulario] = useState<{ id: string; titulo: string } | null>(null);
  const [renderFormulario, setRenderFormulario] = useState<{ 
    id: string; 
    titulo: string; 
    descricao?: string;
    respostaId?: string;
    clientId?: string; // Adicionar clientId ao state
  } | null>(null);
  const [respostasFormulario, setRespostasFormulario] = useState<{ id: string; titulo: string } | null>(null);
  const [distribuicaoFormulario, setDistribuicaoFormulario] = useState<{ id: string; titulo: string } | null>(null);
  const [distribuicoesFormulario, setDistribuicoesFormulario] = useState<{ id: string; titulo: string } | null>(null);
  
  const { campos } = useFormularioCampos(renderFormulario?.id);
  const { createResposta, isCreating } = useFormularioRespostas(renderFormulario?.id);

  const filteredFormularios = formularios.filter((form) =>
    form.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    form.categoria?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'inativo':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'arquivado':
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const handleDelete = () => {
    if (selectedFormulario) {
      deleteFormulario(selectedFormulario.id);
      setDeleteDialogOpen(false);
      setSelectedFormulario(null);
    }
  };

  const handleCreateFormulario = async (data: { titulo: string; descricao: string; categoria: string; client_ids: string[]; pesquisa_satisfacao?: boolean; envio_automatico_fechamento?: boolean }) => {
    try {
      const novoFormulario = await addFormularioAsync({
        titulo: data.titulo,
        descricao: data.descricao,
        categoria: data.categoria,
        status: 'ativo',
        versao: 1,
        client_id: data.client_ids[0], // Primary client
        criado_por: profile?.id,
        pesquisa_satisfacao: data.pesquisa_satisfacao || false,
        envio_automatico_fechamento: data.envio_automatico_fechamento || false,
      });

      // Insert all client associations
      if (data.client_ids.length > 0) {
        await supabase
          .from('formulario_clientes')
          .insert(data.client_ids.map(cid => ({
            formulario_id: novoFormulario.id,
            client_id: cid,
          })));
      }

      const clienteNomes = data.client_ids
        .map(id => clients.find(c => c.id === id)?.name)
        .filter(Boolean)
        .join(', ');

      setNewDialogOpen(false);
      
      toast({
        title: "Formulário criado com sucesso!",
        description: `"${data.titulo}" foi criado e associado a: ${clienteNomes || 'clientes selecionados'}.`,
      });

      setBuilderFormulario({ id: novoFormulario.id, titulo: novoFormulario.titulo });
      setBuilderOpen(true);
    } catch (error) {
      console.error('Erro ao criar formulário:', error);
      toast({
        title: "Erro ao criar formulário",
        description: "Não foi possível criar o formulário. Tente novamente.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateFormulario = (id: string, data: Partial<Formulario>) => {
    updateFormulario({ id, ...data });
    setEditDialogOpen(false);
    setSelectedFormulario(null);
  };

  const handleOpenBuilder = (formulario: Formulario) => {
    setBuilderFormulario({ id: formulario.id, titulo: formulario.titulo });
    setBuilderOpen(true);
  };

  const handleCloseBuilder = () => {
    setBuilderOpen(false);
    setBuilderFormulario(null);
  };

  const handleResponderFormulario = (formulario: Formulario) => {
    setRenderFormulario({
      id: formulario.id,
      titulo: formulario.titulo,
      descricao: formulario.descricao,
      respostaId: undefined,
      clientId: formulario.client_id, // Passar o client_id do formulário
    });
  };

  if (builderOpen && builderFormulario) {
    return (
      <FormularioBuilder
        formularioId={builderFormulario.id}
        formularioTitulo={builderFormulario.titulo}
        onClose={handleCloseBuilder}
      />
    );
  }

  if (renderFormulario && campos) {
    return (
      <FormularioRenderer
        formularioId={renderFormulario.id}
        formularioTitulo={renderFormulario.titulo}
        formularioDescricao={renderFormulario.descricao}
        campos={campos}
        respostaId={renderFormulario.respostaId}
        clientId={renderFormulario.clientId} // Passar clientId
        isPublic={false} // Não é público quando responde dentro do sistema
        onCancel={() => setRenderFormulario(null)}
      />
    );
  }

  if (respostasFormulario) {
    return (
      <FormularioRespostasList
        formularioId={respostasFormulario.id}
        formularioTitulo={respostasFormulario.titulo}
        onClose={() => setRespostasFormulario(null)}
      />
    );
  }

  if (distribuicoesFormulario) {
    return (
      <FormularioDistribuicoesList
        formularioId={distribuicoesFormulario.id}
        onBack={() => setDistribuicoesFormulario(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="w-full max-w-none p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ClipboardList className="h-8 w-8" />
            Formulários
          </h1>
          <p className="text-muted-foreground mt-1">
            Crie e gerencie formulários customizados para sua equipe
          </p>
        </div>
        <Button size="lg" className="gap-2" onClick={() => setNewDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Novo Formulário
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Formulários</CardTitle>
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formularios.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Formulários Ativos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formularios.filter(f => f.status === 'ativo').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Respostas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formularios.reduce((sum, f) => sum + (f.total_respostas || 0), 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar formulários..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Formulários Grid */}
      {filteredFormularios.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Nenhum formulário encontrado</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "Tente ajustar sua busca" : "Comece criando seu primeiro formulário"}
            </p>
            {!searchTerm && (
              <Button className="gap-2" onClick={() => setNewDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Criar Primeiro Formulário
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFormularios.map((formulario) => (
            <Card key={formulario.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1 flex-1">
                    <CardTitle className="text-lg">{formulario.titulo}</CardTitle>
                    {formulario.categoria && (
                      <Badge variant="outline" className="text-xs">
                        {formulario.categoria}
                      </Badge>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => setDistribuicaoFormulario({ id: formulario.id, titulo: formulario.titulo })}
                      >
                        <Send className="h-4 w-4" />
                        Distribuir Formulário
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => setDistribuicoesFormulario({ id: formulario.id, titulo: formulario.titulo })}
                      >
                        <Users className="h-4 w-4" />
                        Ver Distribuições
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => handleOpenBuilder(formulario)}
                      >
                        <Settings className="h-4 w-4" />
                        Editar Campos
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => {
                          setSelectedFormulario(formulario);
                          setEditDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                        Editar Informações
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => handleResponderFormulario(formulario)}
                        disabled={isCreating}
                      >
                        <FileText className="h-4 w-4" />
                        Responder Formulário
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => setRespostasFormulario({ id: formulario.id, titulo: formulario.titulo })}
                      >
                        <Eye className="h-4 w-4" />
                        Ver Respostas ({formulario.total_respostas || 0})
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => duplicarFormulario(formulario.id)}
                      >
                        <Copy className="h-4 w-4" />
                        Duplicar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2"
                        onClick={() => arquivarFormulario(formulario.id)}
                      >
                        <Archive className="h-4 w-4" />
                        Arquivar
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="gap-2 text-destructive"
                        onClick={() => {
                          setSelectedFormulario(formulario);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash className="h-4 w-4" />
                        Deletar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                {formulario.descricao && (
                  <CardDescription className="line-clamp-2">
                    {formulario.descricao}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className={getStatusColor(formulario.status)}>
                      {formulario.status}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Campos</span>
                    <span className="font-medium">{formulario.total_campos || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Respostas</span>
                    <span className="font-medium">{formulario.total_respostas || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-2 border-t">
                    <span className="text-muted-foreground">Criado em</span>
                    <span className="text-xs">
                      {format(new Date(formulario.criado_em), "dd/MM/yyyy", { locale: ptBR })}
                    </span>
                  </div>
                  {((formulario as any).all_clients?.length > 0 || formulario.client) && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Cliente(s)</span>
                      <div className="flex flex-wrap gap-1 justify-end max-w-[200px]">
                        {(formulario as any).all_clients?.length > 0
                          ? (formulario as any).all_clients.map((c: any) => (
                              <Badge key={c.id} variant="outline" className="text-xs">
                                {c.name}
                              </Badge>
                            ))
                          : formulario.client && (
                              <Badge variant="outline" className="text-xs">
                                {formulario.client.name}
                              </Badge>
                            )
                        }
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <NewFormularioDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onSubmit={handleCreateFormulario}
      />

      <EditFormularioDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        formulario={selectedFormulario}
        onSubmit={handleUpdateFormulario}
      />

      {/* Distribuição Dialog */}
      {distribuicaoFormulario && (
        <FormularioDistribuicaoDialog
          formularioId={distribuicaoFormulario.id}
          formularioTitulo={distribuicaoFormulario.titulo}
          open={!!distribuicaoFormulario}
          onClose={() => setDistribuicaoFormulario(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar Formulário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja deletar este formulário? Esta ação não pode ser desfeita
              e todas as respostas associadas serão perdidas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FormulariosManagement;
